import dayjs from 'dayjs';
import { useEffect, useState } from 'react';

import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { initializeTemplateWithMetadata } from '../../utils/initializeTemplateWithMetadata';
import { fetchEngagementLetterTemplate, getOnePagerTemplateData, saveOnePagerTemplateData } from '../../store/investor/investorApiHelper';
import { fetchCompanyOverviewData } from '../../store/dashboard/overview/actions';
import { OverviewData } from '../../store/dashboard/overview/types';
import { closeEngagementLetter, setEngagementLetterData } from '../../store/investor/action';
import { DATE_FORMAT } from '../../utils/constants/commonConstants';

import { Button, Modal } from '../_custom';
import EngagementLetterEditor from './EngagementLetterEditor/EngagementLetterEditor';

import styles from './EngagementLetter.module.scss';

function EngagementLetter() {
    const dispatch = useAppDispatch();
    const [savingData, setSavingData] = useState(false);
    const [userInfo, setUserInfo] = useState({});
    const [editorInitialData, setEditorInitialData] = useState<any>(null);
    const [editorReady, setEditorReady] = useState(false);
    const [showRegenConfirmModal, setShowRegenConfirmModal] = useState(false);

    const companyMainImg = useAppSelector((state) => state.user.domainMetadata?.mainImageUrl);
    const investorOrgId = useAppSelector((state) => state.user.userData.investorOrgId);
    const engagementLetterData = useAppSelector((state) => state.investor.engagementLetter);
    const orgId = engagementLetterData.orgId;
    const source = engagementLetterData.source;
    const emailId = useAppSelector((state) => state.user.userData.primaryEmail);

    useEffect(() => {
        return () => {
            dispatch(closeEngagementLetter());
        };
    }, []);

    useEffect(() => {
        fetchEngagementLetterTemplateData();
    }, [orgId]);

    useEffect(() => {
        if (editorInitialData) {
            setEditorReady(true);
        }
    }, [editorInitialData]);

    const fetchDataForEngagementLetter = async (isRegenerate = false) => {
        if (!isRegenerate) {
            setEditorReady(false);
        }
        dispatch(setEngagementLetterData({ loading: true }));
        dispatch(
            fetchCompanyOverviewData({
                assesseeOrgId: orgId as string,
                investorOrgId: investorOrgId as string,
                onSuccess: async (data: OverviewData, actionCta) => {
                    const elTemplate = await fetchEngagementLetterTemplate({
                        orgId: orgId as string,
                        accountType: 'INVESTOR',
                        pnoId: investorOrgId as string,
                        primaryEmail: emailId as string,
                    });
                    setEditorInitialData(
                        initializeTemplateWithMetadata(elTemplate || [], {
                            pnoLogo: companyMainImg as string,
                            companyName: engagementLetterData.companyName ?? '',
                            companyAddress: data?.headquarterLocation?.fullAddress ?? '',
                            currentDate: dayjs().format(DATE_FORMAT),
                        }),
                    );
                    setEditorReady(true);
                    dispatch(setEngagementLetterData({ loading: false }));
                },
                onError: () => {
                    setEditorReady(true);
                    dispatch(setEngagementLetterData({ loading: false }));
                },
            }),
        );
    };

    const fetchEngagementLetterTemplateData = () => {
        let timeout: NodeJS.Timeout;
        setEditorReady(false);
        dispatch(setEngagementLetterData({ loading: true }));
        getOnePagerTemplateData({
            assesseeOrgId: orgId as string,
            investorOrgId: investorOrgId,
            docType: 'ENGAGEMENT_LETTER',
        })
            .then(({ onePagerData, templateData }) => {
                if (templateData?.length) {
                    setEditorInitialData(templateData);

                    setUserInfo(onePagerData);

                    timeout = setTimeout(() => {
                        setEditorReady(true);
                        dispatch(setEngagementLetterData({ loading: false }));
                    }, 200);
                } else fetchDataForEngagementLetter();
            })
            .catch(() => {});
        return () => clearTimeout(timeout);
    };

    const saveEngagementLetter = (editorContent) => {
        if (savingData) return;
        setSavingData(true);
        saveOnePagerTemplateData({
            assesseeOrgId: orgId as string,
            investorOrgId: investorOrgId,
            onePagerData: editorContent || [],
            email: emailId as string,
            docType: 'ENGAGEMENT_LETTER',
        })
            .then((data) => {
                setTimeout(() => {
                    dispatch(closeEngagementLetter());
                    setSavingData(false);
                }, 1000);
            })
            .catch(() => {
                setSavingData(false);
            });
    };

    return (
        <div>
            {editorReady && (
                <EngagementLetterEditor
                    source={source}
                    generating={engagementLetterData.loading}
                    openModal={editorReady}
                    orgId={orgId}
                    orgName={engagementLetterData.companyName ?? ''}
                    editorInitialData={editorInitialData}
                    editorReady={editorReady}
                    savingData={savingData}
                    saveEngagementLetter={saveEngagementLetter}
                    setShowRegenConfirmModal={setShowRegenConfirmModal}
                    userInfo={userInfo}
                />
            )}

            {showRegenConfirmModal && (
                <Modal open={showRegenConfirmModal} onClose={() => setShowRegenConfirmModal(false)} size="flexible">
                    <div className={styles.ConfirmationModal}>
                        <div className={styles.Title}>Regenerate Engagement Letter</div>
                        <div className={styles.Subtext}>
                            Are you sure you want to regenerate Engagement Letter? You will lose all the changes made so far.
                        </div>
                        <div className={styles.Buttons}>
                            <Button
                                text={'Cancel'}
                                onClick={() => {
                                    setShowRegenConfirmModal(false);
                                }}
                                variant={'purpleinverted'}
                                style={{ marginTop: '0' }}
                            />
                            <Button
                                text={'Proceed Anyway'}
                                onClick={() => {
                                    setShowRegenConfirmModal(false);
                                    fetchDataForEngagementLetter(true);
                                }}
                                style={{ marginTop: '0' }}
                                variant={'primary'}
                            />
                        </div>
                    </div>
                </Modal>
            )}
        </div>
    );
}

export default EngagementLetter;
